import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import { useRouterState } from '@tanstack/react-router';
import { syncSentryUserContext } from './sentry.tags';

export interface FeatureSpanMetadata {
  route: string;
  module: string;
  feature: string;
  op?: string;
}

const routePatterns: Array<{
  matcher: RegExp;
  meta: Omit<FeatureSpanMetadata, 'route'>;
}> = [
  {
    matcher: /^\/(?:_authenticated\/)?(?:my-dashboard|admin-dashboard)/,
    meta: { module: 'dashboard', feature: 'dashboard-load' },
  },
  {
    matcher: /^\/(?:_authenticated\/)?task-management/,
    meta: { module: 'task-management', feature: 'task-management-load' },
  },
  {
    matcher: /^\/(?:_authenticated\/)?tech-systems/,
    meta: { module: 'tech-systems', feature: 'tech-systems-load' },
  },
  {
    matcher: /^\/(?:_authenticated\/)?documents-management/,
    meta: { module: 'documents', feature: 'documents-load' },
  },
];

export function resolveFeatureMetadata(pathname: string): FeatureSpanMetadata {
  const match = routePatterns.find(({ matcher }) => matcher.test(pathname));
  if (!match) {
    return {
      route: pathname,
      module: 'navigation',
      feature: 'route-change',
    };
  }

  return {
    route: pathname,
    module: match.meta.module,
    feature: match.meta.feature,
  };
}

export function startFeatureSpan<T>(
  metadata: FeatureSpanMetadata,
  callback: () => Promise<T> | T,
): Promise<T> | T {
  return Sentry.withScope((scope) =>
    Sentry.startSpanManual(
      {
        op: metadata.op ?? 'ui.feature',
        name: `${metadata.module}.${metadata.feature}`,
      },
      async (span, finish) => {
        scope.setTag('module', metadata.module);
        scope.setTag('feature', metadata.feature);
        scope.setContext('route', {
          pathname: metadata.route,
        });
        span.setAttribute('route', metadata.route);
        span.setAttribute('module', metadata.module);
        span.setAttribute('feature', metadata.feature);

        try {
          return await callback();
        } catch (error) {
          Sentry.captureException(error);
          throw error;
        } finally {
          finish();
        }
      },
    ),
  );
}

export function SentryRouteObserver() {
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });

  useEffect(() => {
    const metadata = resolveFeatureMetadata(pathname);
    syncSentryUserContext();
    Sentry.setTag('module', metadata.module);
    Sentry.setTag('feature', metadata.feature);
    Sentry.setContext('route', {
      pathname: metadata.route,
    });
    Sentry.addBreadcrumb({
      category: 'navigation',
      type: 'navigation',
      level: 'info',
      message: metadata.route,
      data: {
        module: metadata.module,
        feature: metadata.feature,
      },
    });
  }, [pathname]);

  return null;
}
